import type { CatalogState } from '../components/DepartmentCatalog';
import { parseUat, type UatView } from './sb1-uat';

// Candidate contract implemented in held PR #226. These are data endpoints,
// not browser routes. Unknown schema/paths fail closed until integrated.
const REPORT_IDS = new Set(['department-income-statement', 'actuals', 'ar-aging', 'fixed-assets', 'deferred-waterfall', 'income-statement', 'balance-sheet']);
export type CatalogResult = CatalogState & { policyRevision?: string; uat?: { environment: 'sb1'; view: UatView } };
export type CatalogTransport = (path: string, init: RequestInit) => Promise<Response>;
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const label = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0 && v.length <= 200;

export function parseCatalog(value: unknown): CatalogResult {
  if (!record(value) || !label(value.policyRevision) || !Array.isArray(value.departments)
    || value.departments.length > 1 || !Array.isArray(value.pages)
    || value.pages.some((page) => page !== 'growth') || new Set(value.pages).size !== value.pages.length) throw new Error('unsupported catalog');
  const departments = value.departments.map((department) => {
    if (!record(department) || department.id !== 'finance' || !label(department.title)
      || !label(department.description) || !Array.isArray(department.reports)
      || department.reports.length > REPORT_IDS.size) throw new Error('unsupported department');
    const seen = new Set<string>();
    const reports = department.reports.map((report) => {
      if (!record(report) || !label(report.id) || !REPORT_IDS.has(report.id) || seen.has(report.id)
        || !label(report.title) || report.apiPath !== `/api/finance/${report.id}`) throw new Error('unsupported report');
      seen.add(report.id);
      // Do not turn an arbitrary response string into navigation or fetch URLs.
      return { id: report.id, title: report.title };
    });
    return { id: 'finance' as const, title: department.title, description: department.description, reports };
  });
  return { state: 'ready', policyRevision: value.policyRevision, departments, pages: [...value.pages] as ('growth')[],
    ...(value.uat !== undefined ? { uat: parseUat(value.uat) } : {}) };
}

/** No global fetch fallback: preview injects synthetic transport, live wiring is held. */
export function createCatalogLoader(transport: CatalogTransport, publish: (state: CatalogResult) => void) {
  let generation = 0;
  let controller: AbortController | undefined;
  let disposed = false;
  return {
    async refresh() {
      if (disposed) return;
      const request = ++generation;
      controller?.abort();
      const current = new AbortController();
      controller = current;
      // Drop metadata/selection at request START, not only when a denial arrives.
      publish({ state: 'loading' });
      let result: CatalogResult;
      try {
        const response = await transport('/api/warehouse/catalog', {
          method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: current.signal,
          headers: { Accept: 'application/json' },
        });
        if (response.status === 401) result = { state: 'unauthenticated' };
        else if (response.status === 403) result = { state: 'denied' };
        else if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')) {
          result = { state: 'error' };
        } else result = parseCatalog(await response.json());
      } catch { result = { state: 'error' }; }
      // Abort alone is insufficient: a transport or JSON parse may resolve late.
      if (!disposed && request === generation) publish(result);
    },
    dispose() { disposed = true; generation++; controller?.abort(); },
  };
}
